import React from 'react';
import api, { resolveMediaUrl } from '../services/api';
import { toast as toastBus } from '../utils/toastBus';

export default function StaticVideoModal({ open, onClose, projectId, assets, onCreated, onRefresh }) {
  const maxImages = 5;
  const [selectedAudio, setSelectedAudio] = React.useState('');
  const [selectedImages, setSelectedImages] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);

  const audioItems = Array.isArray(assets?.audio) ? assets.audio : [];
  const imageItems = Array.isArray(assets?.images) ? assets.images : [];

  const formatDuration = React.useCallback((ms) => {
    if (!Number.isFinite(Number(ms)) || Number(ms) <= 0) return null;
    const totalSeconds = Math.round(Number(ms) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const firstAudio = audioItems[0];
    const initialAudioId = firstAudio ? String(firstAudio.id || firstAudio.assetId || firstAudio._id || '') : '';
    setSelectedAudio((prev) => prev || initialAudioId);
    setSelectedImages((prev) => {
      if (prev.length) return prev;
      if (!imageItems.length) return [];
      const firstImageId = String(imageItems[0].id || imageItems[0].assetId || imageItems[0]._id || '');
      return firstImageId ? [firstImageId] : [];
    });
  }, [open, audioItems, imageItems]);

  const btn = React.useCallback((bg) => ({
    background: bg,
    color: '#fff',
    border: 0,
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
    minWidth: 120,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  }), []);

  const toast = React.useCallback((type, message) => {
    try { toastBus({ type, message }); } catch {}
  }, []);

  const toggleImage = React.useCallback((id) => {
    setSelectedImages((prev) => {
      const exists = prev.includes(id);
      if (exists) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= maxImages) {
        toast('warning', `Select up to ${maxImages} images`);
        return prev;
      }
      return [...prev, id];
    });
  }, [maxImages, toast]);

  const handleFiles = React.useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const type = String(file.type || '').toLowerCase();
        let assetType = '';
        if (type.startsWith('image/')) assetType = 'image';
        else if (type.startsWith('audio/')) assetType = 'audio';
        else {
          toast('error', `${file.name || 'File'} not supported`);
          continue;
        }
        const res = await api.composerUploadAsset(projectId, file, assetType);
        if (res?.error) {
          toast('error', `Upload failed${res.error ? `: ${res.error}` : ''}`);
          continue;
        }
        if (assetType === 'audio' && res?.item?.id) {
          setSelectedAudio(String(res.item.id));
        }
        if (assetType === 'image' && res?.item?.id) {
          setSelectedImages((prev) => {
            if (prev.includes(String(res.item.id))) return prev;
            if (prev.length >= maxImages) return prev;
            return [...prev, String(res.item.id)];
          });
        }
        if (typeof onRefresh === 'function') {
          await onRefresh();
        }
      }
    } finally {
      setUploading(false);
    }
  }, [projectId, maxImages, toast, onRefresh]);

  const handleDrop = React.useCallback((event) => {
    event.preventDefault();
    if (event.dataTransfer?.files?.length) {
      handleFiles(event.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedAudio) {
      toast('error', 'Select an audio asset');
      return;
    }
    if (!selectedImages.length) {
      toast('error', 'Select at least one image');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { audioAssetId: selectedAudio, imageAssetIds: selectedImages };
      const res = await api.composerCreateAudioImage(projectId, payload);
      if (!res || res.error || res.ok === false) {
        toast('error', res?.error ? `Create failed: ${res.error}` : 'Create failed');
        return;
      }
      toast('success', 'Static video created');
      if (typeof onCreated === 'function') {
        onCreated({ ...res, item: res?.item ? { ...res.item, url: resolveMediaUrl(res.item.url) } : res.item });
      }
      if (typeof onRefresh === 'function') {
        await onRefresh();
      }
      if (typeof onClose === 'function') onClose();
    } finally {
      setSubmitting(false);
    }
  }, [projectId, selectedAudio, selectedImages, toast, onCreated, onRefresh, onClose]);

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}>
      <div style={{ background: '#0b0b0e', border: '1px solid #1f2937', borderRadius: 12, width: 'min(760px, 100%)', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderBottom: '1px solid #1f2937' }}>
          <div style={{ color: '#e5e7eb', fontWeight: 800 }}>Static Video</div>
          <button onClick={onClose} style={btn('#111827')} disabled={submitting || uploading}>Close</button>
        </div>
        <div style={{ padding: 16, display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ color: '#e5e7eb', fontWeight: 700 }}>Upload Assets</div>
            <div
              onDragOver={(event) => { event.preventDefault(); }}
              onDrop={handleDrop}
              style={{ border: '1px dashed #374151', borderRadius: 10, padding: 16, color: '#9ca3af', textAlign: 'center', background: '#0f1115' }}
            >
              {uploading ? 'Uploading…' : 'Drag & drop images or audio here'}
              <div style={{ marginTop: 8 }}>
                <label style={{ cursor: 'pointer', color: '#ff5a1f', fontWeight: 600 }}>
                  Browse files
                  <input
                    type="file"
                    accept="image/*,audio/*"
                    multiple
                    onChange={(e) => handleFiles(e.target.files)}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              <div style={{ marginTop: 8, fontSize: 12 }}>Max {maxImages} images per video.</div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ color: '#e5e7eb', fontWeight: 700 }}>Select Audio</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {audioItems.map((a, idx) => {
                const value = String(a.id || a.assetId || a._id || '');
                const audioUrl = a?.url ? resolveMediaUrl(a.url) : '';
                const label = a.name || a.filename || (audioUrl ? audioUrl.split('/').slice(-1)[0] : `Audio ${idx + 1}`);
                const durationLabel = formatDuration(a?.durationMs);
                return (
                  <label key={value || idx} style={{ display: 'grid', gap: 6, background: '#111827', padding: 10, borderRadius: 8, border: selectedAudio === value ? '2px solid #ff5a1f' : '1px solid #1f2937' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="radio" name="static-audio" value={value} checked={selectedAudio === value} onChange={() => setSelectedAudio(value)} />
                      <div style={{ color: '#e5e7eb', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
                    </div>
                    {a.waveformUrl && (
                      <img src={resolveMediaUrl(a.waveformUrl)} alt="waveform" loading="lazy" decoding="async" style={{ width: '100%', maxHeight: 80, objectFit: 'contain', background: '#0f1115', borderRadius: 6 }} />
                    )}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#9ca3af', fontSize: 11 }}>
                      {durationLabel && <span>{durationLabel}</span>}
                      {a.previewStatus && <span style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{a.previewStatus}</span>}
                      {a.previewGeneratedAt && <span>{new Date(a.previewGeneratedAt).toLocaleString()}</span>}
                    </div>
                  </label>
                );
              })}
              {!audioItems.length && <div style={{ color: '#9ca3af', fontSize: 12 }}>Upload or generate audio to continue.</div>}
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ color: '#e5e7eb', fontWeight: 700 }}>Select Images</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
              {imageItems.map((img, idx) => {
                const value = String(img.id || img.assetId || img._id || '');
                const isChecked = selectedImages.includes(value);
                return (
                  <label key={value || idx} style={{ display: 'grid', gap: 6, background: '#111827', padding: 8, borderRadius: 8, border: isChecked ? '2px solid #ff5a1f' : '1px solid #1f2937' }}>
                    <img
                      src={resolveMediaUrl(img.thumbnailUrl || img.url)}
                      alt="asset"
                      loading="lazy"
                      decoding="async"
                      style={{ width: '100%', borderRadius: 6, height: 96, objectFit: 'cover' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleImage(value)} />
                      <div style={{ color: '#e5e7eb', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name || img.filename || `Image ${idx + 1}`}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#9ca3af', fontSize: 11 }}>
                      {img.previewStatus && <span style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{img.previewStatus}</span>}
                      {img.previewGeneratedAt && <span>{new Date(img.previewGeneratedAt).toLocaleString()}</span>}
                    </div>
                  </label>
                );
              })}
              {!imageItems.length && <div style={{ color: '#9ca3af', fontSize: 12 }}>Upload or generate images to continue.</div>}
            </div>
            <div style={{ color: '#9ca3af', fontSize: 12 }}>Selected {selectedImages.length} / {maxImages}</div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button style={btn('#374151')} onClick={onClose} disabled={submitting || uploading}>Cancel</button>
            <button style={btn('#ff5a1f')} onClick={handleSubmit} disabled={submitting || uploading || !selectedAudio || !selectedImages.length}>
              {submitting ? 'Creating…' : 'Create Static Video'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
